import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { DIAL_CODES, findDialEntry } from "@/lib/dialCodes";

/**
 * Phone input with a country-dial-code dropdown.
 *
 * Stores the full phone in `value` as "<dial> <number>" e.g. "+234 8012345678".
 * Internally splits into (dialCode, localNumber) for display + editing.
 */
export default function PhoneInput({ value, onChange, defaultDial = "+234", testId = "phone-input", placeholder = "8012345678", label = "Phone Number", required = false }) {
  // Parse incoming value into dial + local
  const parsed = parseValue(value, defaultDial);
  const [dial, setDial] = useState(parsed.dial);
  const [local, setLocal] = useState(parsed.local);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Keep state in sync if parent updates value externally
  useEffect(() => {
    const p = parseValue(value, defaultDial);
    if (p.dial !== dial) setDial(p.dial);
    if (p.local !== local) setLocal(p.local);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function emit(newDial, newLocal) {
    const cleaned = (newLocal || "").replace(/[^0-9]/g, "");
    const combined = cleaned ? `${newDial} ${cleaned}` : "";
    onChange(combined);
  }

  function pickDial(entry) {
    setDial(entry.dial);
    setOpen(false);
    setQuery("");
    emit(entry.dial, local);
  }

  function onLocalChange(v) {
    // strip leading 0 and non-digits for cleanliness
    const cleaned = v.replace(/[^0-9]/g, "");
    setLocal(cleaned);
    emit(dial, cleaned);
  }

  const selectedEntry = findDialEntry(dial);
  const filtered = query
    ? DIAL_CODES.filter((c) =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.dial.includes(query) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      )
    : DIAL_CODES;

  return (
    <div>
      {label && <label className="text-xs font-semibold text-neutral-700">{label}{required && " *"}</label>}
      <div ref={wrapRef} className="mt-1.5 relative flex items-stretch gap-2">
        {/* Dial code dropdown trigger */}
        <button
          type="button"
          data-testid={`${testId}-dial-trigger`}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-3 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 transition focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none min-w-[120px]"
        >
          <span className="text-lg leading-none">{selectedEntry.flag}</span>
          <span className="text-sm font-semibold text-neutral-900 tabular">{selectedEntry.dial}</span>
          <ChevronDown size={14} className="text-neutral-400 ml-auto" />
        </button>
        {/* Local number input */}
        <input
          data-testid={`${testId}-local`}
          type="tel"
          inputMode="tel"
          required={required}
          value={local}
          onChange={(e) => onLocalChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition tabular"
        />
        {/* Dropdown menu */}
        {open && (
          <div data-testid={`${testId}-dial-menu`} className="absolute top-full left-0 mt-2 w-80 max-h-80 overflow-hidden bg-white rounded-2xl border border-neutral-200 shadow-xl z-30 flex flex-col animate-fade-up">
            <div className="px-3 py-2.5 border-b border-neutral-100 flex items-center gap-2">
              <Search size={14} className="text-neutral-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code..."
                className="flex-1 text-sm bg-transparent outline-none placeholder-neutral-400"
              />
            </div>
            <div className="overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-neutral-500">No matches</p>
              ) : (
                filtered.map((c) => (
                  <button
                    type="button"
                    key={c.code + c.dial}
                    data-testid={`${testId}-opt-${c.code}`}
                    onClick={() => pickDial(c)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-neutral-50 transition ${c.dial === dial ? "bg-propera-light" : ""}`}
                  >
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className="flex-1 text-neutral-900 truncate">{c.name}</span>
                    <span className="text-neutral-500 font-semibold text-sm tabular">{c.dial}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function parseValue(v, defaultDial) {
  if (!v || typeof v !== "string") return { dial: defaultDial, local: "" };
  const t = v.trim();
  // Try to match a "+XX <rest>" or "+XX<rest>" pattern
  const m = t.match(/^(\+\d{1,4})\s*([\d\s-]*)$/);
  if (m) {
    return { dial: m[1], local: (m[2] || "").replace(/[^0-9]/g, "") };
  }
  // If it doesn't start with +, treat the whole thing as local
  return { dial: defaultDial, local: t.replace(/[^0-9]/g, "") };
}
